/* eslint-disable react/prop-types */
import React, { useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as Actions from "actions";
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Grid,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

const SystemUsageDisplay = (props) => {
  const { adminGetSystemUsage, adminSystemUsage } = props;
  const { isLoading, serviceError, systemUsage } = adminSystemUsage;

  useEffect(() => {
    // Fetch on mount
    adminGetSystemUsage();
    
    // Auto-refresh every 5 minutes
    const interval = setInterval(() => {
      adminGetSystemUsage();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [adminGetSystemUsage]);

  const getStatusColor = () => {
    if (!systemUsage) return "default";
    const percentage = parseFloat(systemUsage.percentage_used);
    if (percentage >= 95) return "error";
    if (percentage >= 85) return "warning";
    if (percentage >= 70) return "info";
    return "success";
  };

  const getStatusIcon = () => {
    if (!systemUsage) return null;
    const percentage = parseFloat(systemUsage.percentage_used);
    if (percentage >= 95) return <ErrorIcon />;
    if (percentage >= 85) return <WarningIcon />;
    if (percentage >= 70) return <TrendingUpIcon />;
    return <CheckCircleIcon />;
  };

  const getStatusText = () => {
    if (!systemUsage) return "Unknown";
    const percentage = parseFloat(systemUsage.percentage_used);
    if (percentage >= 100) return "Limit Reached";
    if (percentage >= 95) return "Critical";
    if (percentage >= 85) return "High Usage";
    if (percentage >= 70) return "Moderate Usage";
    return "Normal";
  };

  const getAlertSeverity = () => {
    if (!systemUsage) return "info";
    const percentage = parseFloat(systemUsage.percentage_used);
    if (percentage >= 95) return "error";
    if (percentage >= 85) return "warning";
    return "info";
  };

  if (isLoading && !systemUsage.total_generations) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={3}>
            <CircularProgress size={40} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (serviceError) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Alert severity="error">
            <Typography variant="body2">{serviceError}</Typography>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!systemUsage) return null;

  const percentage = parseFloat(systemUsage.percentage_used);
  const statusColor = getStatusColor();

  return (
    <Card sx={{ mb: 3, boxShadow: 3 }}>
      <CardContent>
        <Box mb={2}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs>
              <Typography variant="h6" component="div" gutterBottom>
                Virtual Try-On System Usage
              </Typography>
            </Grid>
            <Grid item>
              <Chip
                icon={getStatusIcon()}
                label={getStatusText()}
                color={statusColor}
                size="small"
              />
            </Grid>
          </Grid>
        </Box>

        <Box mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center" p={2} bgcolor="background.paper" borderRadius={1}>
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {systemUsage.total_generations.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Generations
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center" p={2} bgcolor="background.paper" borderRadius={1}>
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {systemUsage.remaining.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Remaining
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center" p={2} bgcolor="background.paper" borderRadius={1}>
                <Typography variant="h4" color="text.primary" fontWeight="bold">
                  {systemUsage.limit.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  System Limit
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box mb={2}>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="text.secondary">
              Usage Progress
            </Typography>
            <Typography variant="body2" fontWeight="bold" color={statusColor}>
              {systemUsage.percentage_used}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(percentage, 100)}
            color={statusColor}
            sx={{ height: 10, borderRadius: 5 }}
          />
        </Box>

        {percentage >= 85 && (
          <Alert severity={getAlertSeverity()} sx={{ mt: 2 }}>
            {percentage >= 100 ? (
              <Typography variant="body2">
                <strong>System limit reached!</strong> No new try-on generations can be
                created until the counter is reset. Contact administrator.
              </Typography>
            ) : percentage >= 95 ? (
              <Typography variant="body2">
                <strong>Critical usage level!</strong> Only{" "}
                {systemUsage.remaining.toLocaleString()} generations remaining. Plan to reset
                counter soon.
              </Typography>
            ) : (
              <Typography variant="body2">
                <strong>High usage detected.</strong> Monitor system usage and prepare for
                counter reset if needed.
              </Typography>
            )}
          </Alert>
        )}

        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="text.secondary">
            Status: {systemUsage.status === "active" ? "Active" : "Limit Reached"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Auto-refreshes every 5 minutes
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const mapStateToProps = ({ adminSystemUsage }) => ({
  adminSystemUsage,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ ...Actions }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(SystemUsageDisplay);
